import React, { useCallback, useEffect, useMemo, useState } from "react";
import AdminService from "../../../api/services/adminService";
import { formatCopyNumber, toFiniteNumber } from "./masterConfigUtils";
import "./MasterTraderConfigModal.css";
import "./MasterFollowersModal.css";

const PAGE_LIMIT_OPTIONS = [20, 50, 100, 500, 1000];

const ACTIVITY_TABS = [
  { key: "followers", label: "Followers", icon: "fa-users" },
  { key: "trades", label: "Trades", icon: "fa-exchange-alt" },
  { key: "pnl", label: "P&L", icon: "fa-chart-line" },
  { key: "followers-pnl", label: "Followers P&L", icon: "fa-hand-holding-usd" },
  { key: "copied", label: "Copied trades", icon: "fa-clone" },
  { key: "wind-down", label: "Wind-down", icon: "fa-flag" },
];

const FOLLOWER_STATUS = [
  { key: "", label: "All" },
  { key: "ACTIVE", label: "Active" },
  { key: "PAUSED", label: "Paused" },
  { key: "STOPPED", label: "Stopped" },
];

const FOLLOWER_PNL_STATUS = [
  { key: "ACTIVE", label: "Active" },
  { key: "PAUSED", label: "Paused" },
  { key: "STOPPED", label: "Stopped" },
  { key: "all", label: "All" },
];

const SPOT_STATUSES = ["", "PENDING", "PARTIALLY EXECUTED", "FILLED", "CANCELLED"];
const FUTURES_STATUSES = ["", "PENDING", "WORKING", "FILLED", "PARTIAL", "CANCELLED", "REJECTED"];

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const formatNum = (value, digits = 8) => formatCopyNumber(value, digits);

const pnlTone = (value) => {
  const n = toFiniteNumber(value);
  if (!Number.isFinite(n) || n === 0) return "";
  return n > 0 ? "mtf-side--buy" : "mtf-side--sell";
};

const PnlCell = ({ value, digits = 4 }) => (
  <span className={pnlTone(value)}>{formatNum(value, digits)}</span>
);

const MtmCell = ({ value, digits = 4 }) => (
  <span className="mtf-mtm" title="Mark-to-market / not booked">
    {formatNum(value, digits)}
  </span>
);

const followerName = (row) => {
  const user = row?.follower || {};
  const name = [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
  return name || "—";
};

const emptyPag = { page: 1, limit: 20, total: 0, totalPages: 1 };

const pairFromOrder = (row) =>
  row?.pairSymbol ||
  row?.symbol ||
  row?.pair ||
  (row?.base_currency && row?.quote_currency
    ? `${row.base_currency}${row.quote_currency}`
    : row?.ask_currency && row?.pay_currency
    ? `${row.ask_currency}/${row.pay_currency}`
    : "—");

const MasterFollowersModal = ({ master, onClose }) => {
  const masterId = master?._id;
  const [panel, setPanel] = useState("followers");

  const [followerStatus, setFollowerStatus] = useState("");
  const [followers, setFollowers] = useState([]);
  const [followerPag, setFollowerPag] = useState(emptyPag);

  const [market, setMarket] = useState("SPOT");
  const [tradeStatus, setTradeStatus] = useState("");
  const [trades, setTrades] = useState([]);
  const [tradePag, setTradePag] = useState(emptyPag);

  const [pnl, setPnl] = useState(null);

  const [tradePnlMarket, setTradePnlMarket] = useState("");
  const [tradePnl, setTradePnl] = useState([]);
  const [tradePnlPag, setTradePnlPag] = useState(emptyPag);

  const [followersPnlStatus, setFollowersPnlStatus] = useState("ACTIVE");
  const [followersPnl, setFollowersPnl] = useState([]);
  const [windDown, setWindDown] = useState(null);

  const [copied, setCopied] = useState([]);
  const [copiedPag, setCopiedPag] = useState(emptyPag);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const applyPag = (setter, pag, fallbackPage, fallbackLimit) => {
    setter((prev) => ({
      ...prev,
      page: pag?.page || fallbackPage,
      limit: pag?.limit ?? fallbackLimit,
      total: pag?.total ?? 0,
      totalPages: pag?.total_pages ?? pag?.totalPages ?? 1,
    }));
  };

  const fetchFollowers = useCallback(async () => {
    setLoading(true);
    setError("");
    const params = { page: followerPag.page, limit: followerPag.limit };
    if (followerStatus) params.status = followerStatus;
    const res = await AdminService.getMasterTraderFollowers(masterId, params);
    setLoading(false);
    if (res?.success) {
      setFollowers(Array.isArray(res.data) ? res.data : []);
      applyPag(setFollowerPag, res.pagination, followerPag.page, followerPag.limit);
    } else {
      setFollowers([]);
      setError(res?.message || "Failed to load followers.");
    }
  }, [masterId, followerPag.page, followerPag.limit, followerStatus]);

  const fetchTrades = useCallback(async () => {
    setLoading(true);
    setError("");
    const params = { market, page: tradePag.page, limit: tradePag.limit };
    if (tradeStatus) params.status = tradeStatus;
    const res = await AdminService.getMasterTraderTrades(masterId, params);
    setLoading(false);
    if (res?.success) {
      const list = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.orders)
        ? res.data.orders
        : Array.isArray(res.data?.trades)
        ? res.data.trades
        : [];
      setTrades(list);
      applyPag(setTradePag, res.pagination, tradePag.page, tradePag.limit);
    } else {
      setTrades([]);
      setError(res?.message || "Failed to load trades.");
    }
  }, [masterId, market, tradePag.page, tradePag.limit, tradeStatus]);

  const fetchPnl = useCallback(async () => {
    setLoading(true);
    setError("");
    const tradeParams = { page: tradePnlPag.page, limit: tradePnlPag.limit };
    if (tradePnlMarket) tradeParams.market = tradePnlMarket;
    const [pnlRes, tradesRes] = await Promise.all([
      AdminService.getMasterTraderPnl(masterId),
      AdminService.getMasterTraderTradesPnl(masterId, tradeParams),
    ]);
    setLoading(false);
    if (pnlRes?.success) {
      setPnl(pnlRes.data || { spot: [], futures: { openPositions: [], totalRealizedPnl: 0 } });
    } else {
      setPnl(null);
    }
    if (tradesRes?.success) {
      setTradePnl(Array.isArray(tradesRes.data) ? tradesRes.data : []);
      applyPag(setTradePnlPag, tradesRes.pagination, tradePnlPag.page, tradePnlPag.limit);
    } else {
      setTradePnl([]);
    }
    const failMsg = !pnlRes?.success
      ? pnlRes?.message || "Failed to load P&L."
      : !tradesRes?.success
      ? tradesRes?.message || "Failed to load trade P&L."
      : "";
    if (failMsg) setError(failMsg);
  }, [masterId, tradePnlMarket, tradePnlPag.page, tradePnlPag.limit]);

  const fetchFollowersPnl = useCallback(async () => {
    setLoading(true);
    setError("");
    const params = {};
    if (followersPnlStatus) params.status = followersPnlStatus;
    const res = await AdminService.getMasterFollowersPnl(masterId, params);
    setLoading(false);
    if (res?.success) {
      setFollowersPnl(Array.isArray(res.data) ? res.data : []);
    } else {
      setFollowersPnl([]);
      setError(res?.message || "Failed to load followers P&L.");
    }
  }, [masterId, followersPnlStatus]);

  const fetchWindDown = useCallback(async () => {
    setLoading(true);
    setError("");
    const res = await AdminService.getMasterWindDown(masterId);
    setLoading(false);
    if (res?.success) {
      setWindDown(res.data || null);
    } else {
      setWindDown(null);
      setError(res?.message || "Failed to load wind-down.");
    }
  }, [masterId]);

  const fetchCopied = useCallback(async () => {
    setLoading(true);
    setError("");
    const res = await AdminService.getMasterCopiedTrades(masterId, {
      page: copiedPag.page,
      limit: copiedPag.limit,
    });
    setLoading(false);
    if (res?.success) {
      setCopied(Array.isArray(res.data) ? res.data : []);
      applyPag(setCopiedPag, res.pagination, copiedPag.page, copiedPag.limit);
    } else {
      setCopied([]);
      setError(res?.message || "Failed to load copied trades.");
    }
  }, [masterId, copiedPag.page, copiedPag.limit]);

  useEffect(() => {
    setFollowerPag((p) => ({ ...p, page: 1 }));
  }, [followerStatus, followerPag.limit]);

  useEffect(() => {
    setTradePag((p) => ({ ...p, page: 1 }));
    setTradeStatus("");
  }, [market]);

  useEffect(() => {
    setTradePag((p) => ({ ...p, page: 1 }));
  }, [tradeStatus, tradePag.limit]);

  useEffect(() => {
    setCopiedPag((p) => ({ ...p, page: 1 }));
  }, [copiedPag.limit]);

  useEffect(() => {
    setTradePnlPag((p) => ({ ...p, page: 1 }));
  }, [tradePnlMarket, tradePnlPag.limit]);

  useEffect(() => {
    if (panel === "followers") fetchFollowers();
  }, [panel, fetchFollowers]);

  useEffect(() => {
    if (panel === "trades") fetchTrades();
  }, [panel, fetchTrades]);

  useEffect(() => {
    if (panel === "pnl") fetchPnl();
  }, [panel, fetchPnl]);

  useEffect(() => {
    if (panel === "followers-pnl") fetchFollowersPnl();
  }, [panel, fetchFollowersPnl]);

  useEffect(() => {
    if (panel === "wind-down") fetchWindDown();
  }, [panel, fetchWindDown]);

  useEffect(() => {
    if (panel === "copied") fetchCopied();
  }, [panel, fetchCopied]);

  const traderLabel =
    [master?.first_name, master?.last_name].filter(Boolean).join(" ").trim() ||
    master?.email_masked ||
    master?.uuid ||
    masterId;

  const statusOptions = market === "FUTURES" ? FUTURES_STATUSES : SPOT_STATUSES;
  const spotRows = Array.isArray(pnl?.spot) ? pnl.spot : [];
  const futuresOpen = Array.isArray(pnl?.futures?.openPositions) ? pnl.futures.openPositions : [];
  const sumSpot = (key) =>
    spotRows.reduce((acc, row) => acc + toFiniteNumber(row[key], 0), 0);
  const spotRealized = pnl?.spotTotals?.totalRealizedPnl ?? sumSpot("realizedPnl");
  const spotUnrealized = pnl?.spotTotals?.totalUnrealizedPnl ?? sumSpot("unrealizedPnl");
  const futuresRealized = pnl?.futures?.totalRealizedPnl;
  const futuresUnrealized =
    pnl?.futures?.totalUnrealizedPnl ??
    futuresOpen.reduce((acc, row) => acc + toFiniteNumber(row.unrealizedPnl, 0), 0);
  const bookedPnl = toFiniteNumber(spotRealized, 0) + toFiniteNumber(futuresRealized, 0);
  const sortedFollowersPnl = useMemo(
    () =>
      [...followersPnl].sort(
        (a, b) =>
          toFiniteNumber(b.realizedPnlFromThisMaster ?? b.realizedPnl, 0) -
          toFiniteNumber(a.realizedPnlFromThisMaster ?? a.realizedPnl, 0)
      ),
    [followersPnl]
  );

  const pagRow = (pag, rowsLen) => {
    const start = rowsLen === 0 ? 0 : (pag.page - 1) * pag.limit + 1;
    const end = rowsLen === 0 ? 0 : (pag.page - 1) * pag.limit + rowsLen;
    return { start, end };
  };

  const Pagination = ({ pag, rowsLen, onPage }) => {
    if (loading || pag.total <= 0) return null;
    const { start, end } = pagRow(pag, rowsLen);
    return (
      <div className="mtf-pagination">
        <span>
          Showing {start}–{end} of {pag.total}
        </span>
        {pag.totalPages > 1 && (
          <div className="mtf-pagination__btns">
            <button type="button" disabled={pag.page <= 1} onClick={() => onPage(pag.page - 1)}>
              Prev
            </button>
            <button
              type="button"
              disabled={pag.page >= pag.totalPages}
              onClick={() => onPage(pag.page + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mtc-overlay" onClick={onClose} role="presentation">
      <div
        className="mtc-modal mtf-modal mtf-modal--wide"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mtf-title"
      >
        <header className="mtc-modal__header">
          <div>
            <h3 id="mtf-title">Master activity</h3>
            <p>
              {traderLabel}
              {master?.uuid ? <span> · {master.uuid}</span> : null}
            </p>
          </div>
          <button type="button" className="mtc-modal__close" onClick={onClose} aria-label="Close">
            <i className="fas fa-times" />
          </button>
        </header>

        <div className="mtc-modal__body">
          <div className="mtf-panel-tabs">
            {ACTIVITY_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`mtf-panel-tab ${panel === tab.key ? "mtf-panel-tab--active" : ""}`}
                onClick={() => {
                  setError("");
                  setPanel(tab.key);
                }}
              >
                <i className={`fas ${tab.icon}`} /> {tab.label}
              </button>
            ))}
          </div>

          {error && (
            <div className="mtc-error">
              <i className="fas fa-exclamation-circle" /> {error}
            </div>
          )}

          {panel === "followers" && (
            <>
              <div className="mtf-toolbar">
                <div className="mtf-tabs">
                  {FOLLOWER_STATUS.map((tab) => (
                    <button
                      key={tab.key || "all"}
                      type="button"
                      className={`mtf-tab ${followerStatus === tab.key ? "mtf-tab--active" : ""}`}
                      onClick={() => setFollowerStatus(tab.key)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                <label className="mtf-limit">
                  Rows
                  <select
                    value={followerPag.limit}
                    onChange={(e) =>
                      setFollowerPag((p) => ({ ...p, limit: Number(e.target.value) || 20, page: 1 }))
                    }
                  >
                    {PAGE_LIMIT_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="mtf-table-wrap">
                <table className="mtf-table">
                  <thead>
                    <tr>
                      <th>Follower</th>
                      <th>UUID</th>
                      <th>Status</th>
                      <th>Allocated</th>
                      <th>Copied trades</th>
                      <th>Started</th>
                      <th>Stopped</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && followers.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="mtf-empty">
                          <i className="fas fa-spinner fa-spin" /> Loading…
                        </td>
                      </tr>
                    ) : followers.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="mtf-empty">
                          No followers in this filter yet.
                        </td>
                      </tr>
                    ) : (
                      followers.map((row) => {
                        const user = row.follower || {};
                        const st = String(row.status || "").toUpperCase();
                        return (
                          <tr key={row._id || `${row.followerId}-${row.startedAt}`}>
                            <td>
                              <div className="mtf-user">
                                <strong>{followerName(row)}</strong>
                                <span>{user.email || "—"}</span>
                              </div>
                            </td>
                            <td>
                              <code>{user.uuid || "—"}</code>
                            </td>
                            <td>
                              <span className={`mtf-status mtf-status--${st.toLowerCase() || "unknown"}`}>
                                {st || "—"}
                              </span>
                            </td>
                            <td>{formatNum(row.allocatedAmount, 4)}</td>
                            <td>{row.totalCopiedTrades ?? 0}</td>
                            <td>{formatDate(row.startedAt)}</td>
                            <td>{formatDate(row.stoppedAt)}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination
                pag={followerPag}
                rowsLen={followers.length}
                onPage={(page) => setFollowerPag((p) => ({ ...p, page }))}
              />
            </>
          )}

          {panel === "trades" && (
            <>
              <div className="mtf-toolbar">
                <div className="mtf-tabs">
                  {["SPOT", "FUTURES"].map((m) => (
                    <button
                      key={m}
                      type="button"
                      className={`mtf-tab ${market === m ? "mtf-tab--active" : ""}`}
                      onClick={() => setMarket(m)}
                    >
                      {m}
                    </button>
                  ))}
                  <select
                    className="mtf-status-select"
                    value={tradeStatus}
                    onChange={(e) => setTradeStatus(e.target.value)}
                  >
                    {statusOptions.map((s) => (
                      <option key={s || "all"} value={s}>
                        {s || "All statuses"}
                      </option>
                    ))}
                  </select>
                </div>
                <label className="mtf-limit">
                  Rows
                  <select
                    value={tradePag.limit}
                    onChange={(e) =>
                      setTradePag((p) => ({ ...p, limit: Number(e.target.value) || 20, page: 1 }))
                    }
                  >
                    {PAGE_LIMIT_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <p className="mtf-hint">
                Live order status from the {market === "FUTURES" ? "futures" : "spot"} engine — not a cached
                snapshot.
              </p>
              <div className="mtf-table-wrap">
                <table className="mtf-table">
                  <thead>
                    <tr>
                      <th>Pair</th>
                      <th>Side</th>
                      <th>Type</th>
                      <th>Price</th>
                      <th>Qty</th>
                      <th>Filled</th>
                      <th>Remaining</th>
                      <th>Avg</th>
                      <th>Status</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && trades.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="mtf-empty">
                          <i className="fas fa-spinner fa-spin" /> Loading…
                        </td>
                      </tr>
                    ) : trades.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="mtf-empty">
                          No {market.toLowerCase()} orders.
                        </td>
                      </tr>
                    ) : (
                      trades.map((row, idx) => {
                        const side = String(row.side || "").toUpperCase();
                        const filled = row.filled ?? row.filledQty ?? row.filled_quantity;
                        const remaining = row.remaining ?? row.remainingQty;
                        const avg =
                          row.avg_execution_price ?? row.avgFillPrice ?? row.avg_fill_price;
                        const qty = row.quantity ?? row.qty ?? row.origQty;
                        return (
                          <tr key={row._id || row.orderId || row.order_id || idx}>
                            <td>{pairFromOrder(row)}</td>
                            <td className={side === "BUY" || side === "LONG" ? "mtf-side--buy" : "mtf-side--sell"}>
                              {side || "—"}
                            </td>
                            <td>{row.order_type || row.orderType || row.type || "—"}</td>
                            <td>{formatNum(row.price)}</td>
                            <td>{formatNum(qty)}</td>
                            <td>{formatNum(filled)}</td>
                            <td>{formatNum(remaining)}</td>
                            <td>{formatNum(avg)}</td>
                            <td>
                              <span className="mtf-status mtf-status--order">{row.status || "—"}</span>
                            </td>
                            <td>{formatDate(row.createdAt || row.created_at || row.time)}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination
                pag={tradePag}
                rowsLen={trades.length}
                onPage={(page) => setTradePag((p) => ({ ...p, page }))}
              />
            </>
          )}

          {panel === "pnl" && (
            <>
              {loading && !pnl ? (
                <div className="mtf-empty-block">
                  <i className="fas fa-spinner fa-spin" /> Loading P&L…
                </div>
              ) : (
                <>
                  <div className="mtf-toolbar">
                    <p className="mtf-hint" style={{ margin: 0 }}>
                      Booked P&amp;L is realized only (spot SELL and futures closes). Unrealized is
                      mark-to-market for ops — not paid profit and not commission earned.
                    </p>
                    <button
                      type="button"
                      className="mtf-refresh"
                      onClick={fetchPnl}
                      disabled={loading}
                    >
                      <i className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`} /> Refresh
                    </button>
                  </div>
                  <div className="mtf-tiles">
                    <div className="mtf-tile mtf-tile--primary">
                      <div className="mtf-tile__label">Booked P&amp;L</div>
                      <div className={`mtf-tile__value ${pnlTone(bookedPnl)}`}>
                        {formatNum(bookedPnl, 4)}
                      </div>
                      <div className="mtf-tile__live">spot + futures realized</div>
                    </div>
                    <div className="mtf-tile mtf-tile--primary">
                      <div className="mtf-tile__label">Spot realized</div>
                      <div className={`mtf-tile__value ${pnlTone(spotRealized)}`}>
                        {formatNum(spotRealized, 4)}
                      </div>
                    </div>
                    <div className="mtf-tile mtf-tile--primary">
                      <div className="mtf-tile__label">Futures realized</div>
                      <div className={`mtf-tile__value ${pnlTone(futuresRealized)}`}>
                        {formatNum(futuresRealized, 4)}
                      </div>
                    </div>
                    <div className="mtf-tile mtf-tile--mtm">
                      <div className="mtf-tile__label">Spot unrealized (not booked)</div>
                      <div className="mtf-tile__value">{formatNum(spotUnrealized, 4)}</div>
                      <div className="mtf-tile__live">Mark-to-market</div>
                    </div>
                    <div className="mtf-tile mtf-tile--mtm">
                      <div className="mtf-tile__label">Futures unrealized (not booked)</div>
                      <div className="mtf-tile__value">{formatNum(futuresUnrealized, 4)}</div>
                      <div className="mtf-tile__live">Mark-to-market</div>
                    </div>
                    <div className="mtf-tile">
                      <div className="mtf-tile__label">Open futures</div>
                      <div className="mtf-tile__value">{futuresOpen.length}</div>
                      <div className="mtf-tile__live">positions</div>
                    </div>
                  </div>
                  <div className="mtf-pnl-grid">
                    <section className="mtf-pnl-card">
                      <h4>Spot by pair</h4>
                      <div className="mtf-table-wrap">
                        <table className="mtf-table">
                          <thead>
                            <tr>
                              <th>Pair</th>
                              <th>Held</th>
                              <th>Avg cost</th>
                              <th>Current</th>
                              <th>Realized</th>
                              <th>Unrealized (not booked)</th>
                              <th>Bought</th>
                              <th>Sold</th>
                            </tr>
                          </thead>
                          <tbody>
                            {spotRows.length === 0 ? (
                              <tr>
                                <td colSpan={8} className="mtf-empty">
                                  No spot P&L yet.
                                </td>
                              </tr>
                            ) : (
                              spotRows.map((row) => (
                                <tr key={row.pairSymbol || `${row.baseCurrencyId}-${row.quoteCurrencyId}`}>
                                  <td>{row.pairSymbol || "—"}</td>
                                  <td>{formatNum(row.quantityHeld)}</td>
                                  <td>{formatNum(row.averageCostPrice)}</td>
                                  <td>{formatNum(row.currentPrice)}</td>
                                  <td><PnlCell value={row.realizedPnl} /></td>
                                  <td><MtmCell value={row.unrealizedPnl} /></td>
                                  <td>{formatNum(row.totalBuyQuantity)}</td>
                                  <td>{formatNum(row.totalSellQuantity)}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </section>
                    <section className="mtf-pnl-card">
                      <h4>Futures open positions</h4>
                      <p className="mtf-pnl-total">
                        Realized <PnlCell value={futuresRealized} /> · Unrealized (not booked){" "}
                        <MtmCell value={futuresUnrealized} />
                      </p>
                      <div className="mtf-table-wrap">
                        <table className="mtf-table">
                          <thead>
                            <tr>
                              <th>Symbol</th>
                              <th>Side</th>
                              <th>Qty</th>
                              <th>Entry</th>
                              <th>Mark</th>
                              <th>Unrealized (not booked)</th>
                              <th>Leverage</th>
                            </tr>
                          </thead>
                          <tbody>
                            {futuresOpen.length === 0 ? (
                              <tr>
                                <td colSpan={7} className="mtf-empty">
                                  No open futures positions.
                                </td>
                              </tr>
                            ) : (
                              futuresOpen.map((row, idx) => (
                                <tr key={`${row.symbol}-${row.side}-${idx}`}>
                                  <td>{row.symbol || "—"}</td>
                                  <td className={String(row.side).toUpperCase() === "LONG" ? "mtf-side--buy" : "mtf-side--sell"}>
                                    {row.side || "—"}
                                  </td>
                                  <td>{formatNum(row.quantity)}</td>
                                  <td>{formatNum(row.entryPrice)}</td>
                                  <td>{formatNum(row.lastMarkPrice)}</td>
                                  <td><MtmCell value={row.unrealizedPnl} /></td>
                                  <td>{row.leverage ?? "—"}x</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </section>
                  </div>

                  <h4 className="mtf-section-title">Trades</h4>
                  <div className="mtf-toolbar">
                    <div className="mtf-tabs">
                      {[
                        { key: "", label: "All" },
                        { key: "SPOT", label: "Spot" },
                        { key: "FUTURES", label: "Futures" },
                      ].map((tab) => (
                        <button
                          key={tab.key || "all"}
                          type="button"
                          className={`mtf-tab ${tradePnlMarket === tab.key ? "mtf-tab--active" : ""}`}
                          onClick={() => setTradePnlMarket(tab.key)}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>
                    <label className="mtf-limit">
                      Rows
                      <select
                        value={tradePnlPag.limit}
                        onChange={(e) =>
                          setTradePnlPag((p) => ({
                            ...p,
                            limit: Number(e.target.value) || 20,
                            page: 1,
                          }))
                        }
                      >
                        {PAGE_LIMIT_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <p className="mtf-hint">
                    Booked P&amp;L is realized SELL / closed futures only. Open BUY / unrealized rows
                    show Open — not live mark-to-market profit.
                  </p>
                  <div className="mtf-table-wrap">
                    <table className="mtf-table">
                      <thead>
                        <tr>
                          <th>Pair / Symbol</th>
                          <th>Market</th>
                          <th>Side</th>
                          <th>Qty</th>
                          <th>Price</th>
                          <th>P&amp;L</th>
                          <th>P&amp;L type</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading && tradePnl.length === 0 ? (
                          <tr>
                            <td colSpan={8} className="mtf-empty">
                              <i className="fas fa-spinner fa-spin" /> Loading…
                            </td>
                          </tr>
                        ) : tradePnl.length === 0 ? (
                          <tr>
                            <td colSpan={8} className="mtf-empty">
                              No trade P&amp;L rows yet.
                            </td>
                          </tr>
                        ) : (
                          tradePnl.map((row, idx) => {
                            const side = String(row.side || "").toUpperCase();
                            const unrealized = String(row.pnlType || "").toUpperCase() === "UNREALIZED";
                            return (
                              <tr
                                key={row.orderId || `${row.market}-${row.createdAt}-${idx}`}
                                className={unrealized ? "mtf-row--mtm" : undefined}
                              >
                                <td>{row.pairSymbol || row.symbol || "—"}</td>
                                <td>{row.market || "—"}</td>
                                <td className={side === "BUY" || side === "LONG" ? "mtf-side--buy" : "mtf-side--sell"}>
                                  {side || "—"}
                                </td>
                                <td>{formatNum(row.quantity)}</td>
                                <td>
                                  {formatNum(row.price)}
                                  {row.averageCostAtFill != null ? (
                                    <span className="mtf-now">avg {formatNum(row.averageCostAtFill)}</span>
                                  ) : null}
                                </td>
                                <td>
                                  {unrealized ? (
                                    <span className="mtf-mtm" title="Mark-to-market / not booked">
                                      Open
                                    </span>
                                  ) : (
                                    <PnlCell value={row.pnl} />
                                  )}
                                </td>
                                <td>
                                  {unrealized ? (
                                    <span
                                      className="mtf-status mtf-badge--mtm"
                                      title="Mark-to-market / not booked. Not paid profit."
                                    >
                                      Not booked
                                    </span>
                                  ) : (
                                    <span className="mtf-status mtf-badge--realized">Realized</span>
                                  )}
                                </td>
                                <td>{formatDate(row.createdAt)}</td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                  <Pagination
                    pag={tradePnlPag}
                    rowsLen={tradePnl.length}
                    onPage={(page) => setTradePnlPag((p) => ({ ...p, page }))}
                  />
                </>
              )}
            </>
          )}

          {panel === "followers-pnl" && (
            <>
              <div className="mtf-toolbar">
                <div className="mtf-tabs">
                  {FOLLOWER_PNL_STATUS.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      className={`mtf-tab ${followersPnlStatus === tab.key ? "mtf-tab--active" : ""}`}
                      onClick={() => setFollowersPnlStatus(tab.key)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className="mtf-refresh"
                  onClick={fetchFollowersPnl}
                  disabled={loading}
                >
                  <i className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`} /> Refresh
                </button>
              </div>
              <p className="mtf-hint">
                Follower profit from this master is realized P&amp;L only (booked sells/closes).
                Unrealized is mark-to-market / not booked — it does not mean the follower made profit
                or that commission is due. Commission is an estimate, not charged.
              </p>
              <div className="mtf-table-wrap">
                <table className="mtf-table">
                  <thead>
                    <tr>
                      <th>Follower</th>
                      <th>Allocated</th>
                      <th>Status</th>
                      <th>Copied trades</th>
                      <th>Realized P&amp;L</th>
                      <th>Unrealized (not booked)</th>
                      <th>Commission (est.)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && followersPnl.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="mtf-empty">
                          <i className="fas fa-spinner fa-spin" /> Loading…
                        </td>
                      </tr>
                    ) :                       followersPnl.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="mtf-empty">
                          No follower P&amp;L in this filter.
                        </td>
                      </tr>
                    ) : (
                      sortedFollowersPnl.map((row) => {
                        const user = row.follower || {};
                        const name = [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
                        const st = String(row.status || "").toUpperCase();
                        const est = row.commissionEstimate || {};
                        const realized = row.realizedPnlFromThisMaster ?? row.realizedPnl;
                        const unrealized = row.unrealizedPnlFromThisMaster ?? row.unrealizedPnl;
                        return (
                          <tr key={row.followerId || user.uuid}>
                            <td>
                              <div className="mtf-user">
                                <strong>{name || "—"}</strong>
                                <span>{user.email || "—"}</span>
                                {user.uuid != null ? <span>{user.uuid}</span> : null}
                              </div>
                            </td>
                            <td>{formatNum(row.allocatedAmount, 4)}</td>
                            <td>
                              <span className={`mtf-status mtf-status--${st.toLowerCase() || "unknown"}`}>
                                {st || "—"}
                              </span>
                            </td>
                            <td>{row.copiedTradeCount ?? 0}</td>
                            <td className="mtf-realized-cell">
                              <PnlCell value={realized} />
                            </td>
                            <td>
                              <MtmCell value={unrealized} />
                              <span className="mtf-now">not booked</span>
                            </td>
                            <td>
                              <div
                                className="mtf-est"
                                title="Estimate only — not charged. Unrealized P&L does not mean commission is due."
                              >
                                <span>{formatNum(est.totalCommission, 4)}</span>
                                <span className="mtf-est__tag">Estimate — not charged</span>
                                <span className="mtf-now">
                                  master {formatNum(est.masterShare, 4)}
                                  {est.masterSharePercentage != null
                                    ? ` (${formatNum(est.masterSharePercentage, 2)}%)`
                                    : ""}
                                  {" · "}
                                  platform {formatNum(est.platformShare, 4)}
                                  {est.platformSharePercentage != null
                                    ? ` (${formatNum(est.platformSharePercentage, 2)}%)`
                                    : ""}
                                </span>
                                <span className="mtf-now">
                                  HWM profit {formatNum(est.profitSubjectToShare, 4)} · not a booked charge
                                </span>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {panel === "copied" && (
            <>
              <div className="mtf-toolbar">
                <p className="mtf-hint" style={{ margin: 0 }}>
                  Mirror-attempt status (PLACED / FAILED / SKIPPED) plus live follower order state.
                  Skip reasons include NO_MIRRORED_POSITION, INSUFFICIENT_BALANCE,
                  ORDER_TYPE_NOT_ALLOWED, MAX_OPEN_POSITIONS_REACHED, BELOW_MIN_SIZE. Sells/closes
                  are position-based; paused followers still receive closes.
                </p>
                <label className="mtf-limit">
                  Rows
                  <select
                    value={copiedPag.limit}
                    onChange={(e) =>
                      setCopiedPag((p) => ({ ...p, limit: Number(e.target.value) || 20, page: 1 }))
                    }
                  >
                    {PAGE_LIMIT_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="mtf-table-wrap">
                <table className="mtf-table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Market</th>
                      <th>Pair</th>
                      <th>Side</th>
                      <th>Type</th>
                      <th>Master qty</th>
                      <th>Master price</th>
                      <th>Close</th>
                      <th>Follower orders</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && copied.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="mtf-empty">
                          <i className="fas fa-spinner fa-spin" /> Loading…
                        </td>
                      </tr>
                    ) : copied.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="mtf-empty">
                          No copied trades yet.
                        </td>
                      </tr>
                    ) : (
                      copied.map((row) => {
                        const side = String(row.side || "").toUpperCase();
                        const followers = Array.isArray(row.followerOrders) ? row.followerOrders : [];
                        return (
                          <tr key={row._id || row.masterOrderId}>
                            <td>{formatDate(row.createdAt)}</td>
                            <td>{row.market || "—"}</td>
                            <td>{row.pairSymbol || "—"}</td>
                            <td className={side === "BUY" || side === "LONG" ? "mtf-side--buy" : "mtf-side--sell"}>
                              {side || "—"}
                            </td>
                            <td>{row.orderType || "—"}</td>
                            <td>{formatNum(row.masterQuantity)}</td>
                            <td>{formatNum(row.masterPrice)}</td>
                            <td>{row.isCloseOrReduce ? "Yes" : "No"}</td>
                            <td>
                              {followers.length === 0 ? (
                                "—"
                              ) : (
                                <ul className="mtf-follow-list">
                                  {followers.map((fo, i) => {
                                    const live = fo.liveStatus;
                                    const attempt = String(fo.status || "").toUpperCase();
                                    return (
                                      <li key={fo.followerOrderId || `${fo.followerId}-${i}`}>
                                        <code>{fo.followerId || "—"}</code>
                                        <span> qty {formatNum(fo.quantity)}</span>
                                        <span className={`mtf-status mtf-status--${attempt.toLowerCase() || "unknown"}`}>
                                          {attempt || "—"}
                                        </span>
                                        {live ? (
                                          <span className="mtf-live">
                                            live {live.status || "—"} · filled {formatNum(live.filled)} · rem{" "}
                                            {formatNum(live.remaining)} · avg {formatNum(live.avgPrice)}
                                          </span>
                                        ) : (
                                          <span className="mtf-live">no live status</span>
                                        )}
                                        {fo.reason ? <span className="mtf-reason">{fo.reason}</span> : null}
                                      </li>
                                    );
                                  })}
                                </ul>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination
                pag={copiedPag}
                rowsLen={copied.length}
                onPage={(page) => setCopiedPag((p) => ({ ...p, page }))}
              />
            </>
          )}

          {panel === "wind-down" && (
            <>
              <div className="mtf-toolbar">
                <p className="mtf-hint" style={{ margin: 0 }}>
                  Demotion with active or paused followers runs a wind-down instead of a bare role
                  flip. Poll progress here; retry or force from Revert on the traders list.
                </p>
                <button
                  type="button"
                  className="mtf-refresh"
                  onClick={fetchWindDown}
                  disabled={loading}
                >
                  <i className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`} /> Refresh
                </button>
              </div>
              {loading && !windDown ? (
                <div className="mtf-empty-block">
                  <i className="fas fa-spinner fa-spin" /> Loading wind-down…
                </div>
              ) : !windDown ? (
                <div className="mtf-empty-block">No wind-down record for this master.</div>
              ) : (
                <>
                  <div className="mtf-tiles">
                    <div className="mtf-tile">
                      <div className="mtf-tile__label">Status</div>
                      <div className="mtf-tile__value">{windDown.status || "—"}</div>
                    </div>
                    <div className="mtf-tile">
                      <div className="mtf-tile__label">In progress</div>
                      <div className="mtf-tile__value">
                        {windDown.windDown?.inProgress ? "Yes" : "No"}
                      </div>
                    </div>
                    <div className="mtf-tile">
                      <div className="mtf-tile__label">Followers</div>
                      <div className="mtf-tile__value">
                        {windDown.windDown?.followersCompleted ?? 0}/
                        {windDown.windDown?.followersTotal ?? 0}
                      </div>
                    </div>
                    <div className="mtf-tile">
                      <div className="mtf-tile__label">Force</div>
                      <div className="mtf-tile__value">
                        {windDown.windDown?.force ? "Yes" : "No"}
                      </div>
                    </div>
                  </div>
                  <p className="mtf-hint">
                    {windDown.windDown?.reason ? <>Reason: {windDown.windDown.reason}. </> : null}
                    Started {formatDate(windDown.windDown?.startedAt)} · completed{" "}
                    {formatDate(windDown.windDown?.completedAt)}.
                  </p>
                  <div className="mtf-table-wrap">
                    <table className="mtf-table">
                      <thead>
                        <tr>
                          <th>Follower</th>
                          <th>Preference</th>
                          <th>Status</th>
                          <th>Closed</th>
                          <th>Settlement</th>
                          <th>Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {!(windDown.windDown?.followerResults || []).length ? (
                          <tr>
                            <td colSpan={6} className="mtf-empty">
                              No follower results yet.
                            </td>
                          </tr>
                        ) : (
                          (windDown.windDown.followerResults || []).map((row) => (
                            <tr key={row.followerId}>
                              <td><code>{row.followerId || "—"}</code></td>
                              <td>{row.preference || "—"}</td>
                              <td>
                                <span
                                  className={`mtf-status mtf-status--${String(row.status || "")
                                    .toLowerCase()
                                    .replace("_", "-")}`}
                                >
                                  {row.status || "—"}
                                </span>
                              </td>
                              <td>
                                {Array.isArray(row.closedPositions)
                                  ? row.closedPositions.length
                                  : row.closedPositions ?? "—"}
                              </td>
                              <td><code>{row.settlementRecordId || "—"}</code></td>
                              <td>{row.error || "—"}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MasterFollowersModal;
